import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { t } from '../../../../../utils/i18-next';
import { isDefined } from '../../../../../utils/variables';
import { ProjectGeodataManager } from '../../../../../viewer/project-geodata-manager';
import { OrthophotoComparisonTool } from '../../../../../viewer/tools/orthophoto-comparison-tool';
import {
  getTimelineOrthotileInfo,
  TimelineOrthotileInfo,
  useProjectOrthotiles,
} from '../../../../hooks/use-project-orthotiles';
import { DropdownSuggestion } from '../../../dropdown-suggestion/dropdown-suggesion';
import { InfoBox } from '../../../info-box/info-box';
import { LabelledContainer } from '../../../labelled-container/labelled-container';
import { RightPanelHeader } from '../../../skyview/right-menu/components/right-menu-header';
import { RightPanelContent } from '../../../skyview/right-menu/components/styles';
import { Stack } from '../../../stack/stack';

interface Props {
  tool: OrthophotoComparisonTool;
}

const CompareOrthotile = (props: Props) => {
  const data = useProjectOrthotiles();
  const { t } = useTranslation();
  const placeHolderText = useMemo(
    () => (data ? t('search', { ns: 'common' }) : t('loading', { ns: 'common' })),
    [t, data],
  );

  const orthoTiles = useMemo<TimelineOrthotileInfo[]>(() => {
    if (!isDefined(data)) {
      return [];
    }

    return data
      .map((x) => getTimelineOrthotileInfo(x, t))
      .filter(isDefined)
      .sort((a, b) => a.index - b.index);
  }, [data, t]);

  const [leftIndex, setLeftIndex] = useState<string>();
  const [leftOptionLoading, setLeftOptionLoading] = useState(false);
  const [rightIndex, setRightIndex] = useState<string>();
  const [rightOptionLoading, setRightOptionLoading] = useState(false);

  const setLeftSide = useCallback(
    async (item: (typeof orthoTiles)[0]) => {
      try {
        setLeftOptionLoading(true);
        setLeftIndex(item.id);
        await props.tool.setLeftSide(item.orthotileId, item.id);
      } finally {
        setLeftOptionLoading(false);
      }
    },
    [props.tool],
  );

  const setRightSide = useCallback(
    async (item: (typeof orthoTiles)[0]) => {
      try {
        setRightOptionLoading(true);
        setRightIndex(item.id);
        await props.tool.setRightSide(item.orthotileId, item.id);
      } finally {
        setRightOptionLoading(false);
      }
    },
    [props.tool],
  );

  useEffect(() => {
    const callback = () => (props.tool.resolution = window.innerWidth);
    window.addEventListener('resize', callback);
    return () => {
      window.removeEventListener('resize', callback);
    };
  }, [props.tool]);

  useEffect(() => {
    const currentTimelineContainer = ProjectGeodataManager.instance().getActiveTimelineContainer();

    // Load current and last container if nothing is loaded.
    if (currentTimelineContainer && !props.tool.leftQuad && !props.tool.rightQuad) {
      const matchingIndex = orthoTiles.findIndex(
        (x) => x.index === currentTimelineContainer.containerIndex,
      );

      if (matchingIndex >= 0) {
        void setLeftSide(orthoTiles[matchingIndex]);
      }
      if (matchingIndex > 0) {
        void setRightSide(orthoTiles[matchingIndex - 1]);
      }

      return;
    }
  }, [orthoTiles, props.tool, setLeftSide, setRightSide]);

  return (
    <Stack spacing={1}>
      <LabelledContainer text={t('left', { ns: 'common' })}>
        <DropdownSuggestion
          disabled={leftOptionLoading || !data}
          placeholder={placeHolderText}
          portalToBody={true}
          suggestions={orthoTiles}
          value={leftIndex}
          width={'100%'}
          onSuggestionSelected={setLeftSide}
        />
      </LabelledContainer>
      <LabelledContainer text={t('right', { ns: 'common' })}>
        <DropdownSuggestion
          disabled={rightOptionLoading || !data}
          placeholder={placeHolderText}
          portalToBody={true}
          suggestions={orthoTiles}
          value={rightIndex}
          width={'100%'}
          onSuggestionSelected={setRightSide}
        />
      </LabelledContainer>
      {(leftOptionLoading || rightOptionLoading) && (
        <InfoBox color="yellow" leftIcon={{ icon: ['fad', 'spinner'] }} leftIconSpin={true}>
          {t('compareOrthophotos.loading', { ns: 'skyviewToolbar' })}
        </InfoBox>
      )}
      {!leftOptionLoading && !rightOptionLoading && (
        <InfoBox color="yellow" leftIcon={{ icon: ['fad', 'info'] }}>
          {t('compareOrthophotos.rightClickInfo', { ns: 'skyviewToolbar' })}
        </InfoBox>
      )}
    </Stack>
  );
};

export const CompareOrthophotosMenu = (props: Props) => {
  return (
    <Stack direction="column" spacing={0}>
      <RightPanelHeader
        icon={['fal', 'map']}
        name={t('compareOrthophotos.tooltip.title', { ns: 'skyviewToolbar' })}
      />
      <RightPanelContent useTopPadding={true}>
        <CompareOrthotile tool={props.tool} />
      </RightPanelContent>
    </Stack>
  );
};
